import { Box, Paper, Typography } from "@mui/material";
import { Recipe } from "../../models/Recipe";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRecipeThumbnail } from "../../utils/RecipeService";
import { formatCookTime } from "../../utils/formatCookTime";

export type RecipeItemProps = {
  recipe: Recipe;
}

/** How many of the three signal bars are filled, per difficulty. */
const DIFFICULTY_LEVEL: Record<string, number> = { easy: 1, medium: 2, hard: 3 };
const BAR_HEIGHTS = [6, 9, 12];

const RecipeItem = (props: RecipeItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recipeImage, setRecipeImage] = useState<string>();
  const objectUrlRef = useRef<string>();

  const difficulty = props.recipe.difficulty?.toLowerCase();
  const difficultyLevel = difficulty ? DIFFICULTY_LEVEL[difficulty] : undefined;
  const cookTimeLabel = formatCookTime(props.recipe.cookTime, t);

  useEffect(() => {
    let cancelled = false;
    getRecipeThumbnail(props.recipe.id!).then((image: Blob | null) => {
      if (cancelled || !image || image.size === 0) return;
      const url = URL.createObjectURL(image);
      objectUrlRef.current = url;
      setRecipeImage(url);
    });

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = undefined;
      }
    };
  }, [props.recipe.id]);

  return (
    <Paper
      elevation={0}
      onClick={() => navigate('/recipe/' + props.recipe.id)}
      sx={{
        width: '100%',
        cursor: 'pointer',
        borderRadius: '18px',
        p: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: (theme) => theme.palette.mode === 'light'
          ? '0 1px 2px rgba(180, 83, 9, 0.06), 0 0 0 1px rgba(224, 189, 140, 0.4)'
          : '0 1px 2px rgba(0,0,0,0.35), 0 0 0 1px #3D2A15',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.palette.mode === 'light'
            ? '0 8px 24px rgba(180, 83, 9, 0.18)'
            : '0 8px 24px rgba(0,0,0,0.5)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
      }}
    >
      {/* Inset thumbnail - fixed square, so a row never changes height and the image never bleeds */}
      <Box
        sx={{
          flex: '0 0 78px',
          height: 78,
          borderRadius: '13px',
          overflow: 'hidden',
          backgroundColor: 'divider',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {recipeImage ? (
          <Box
            component="img"
            src={recipeImage}
            alt={props.recipe.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <RestaurantIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography
          noWrap
          sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.25, color: 'text.primary' }}
        >
          {props.recipe.name}
        </Typography>

        {/* Metadata as one quiet line: time - portions - difficulty */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Wraps rather than clips: longer translations ("1 Std. 30 Min. - 8 Portionen -
            // Einfach") do not fit on one line at phone widths. A second line still sits well
            // inside the 78px thumbnail, so the row height does not change either way.
            flexWrap: 'wrap',
            columnGap: 1,
            rowGap: 0.25,
            whiteSpace: 'nowrap',
            fontWeight: 500,
            fontSize: '0.76rem',
            color: (theme) => theme.palette.mode === 'light' ? '#8A7C6D' : theme.palette.text.secondary,
          }}
        >
          {cookTimeLabel && <span>{cookTimeLabel}</span>}
          {cookTimeLabel && <Separator />}
          <span>{props.recipe.yield} {t('portions')}</span>

          {difficulty && (
            <>
              <Separator />
              {/* Meter and label stay one flex item so a wrap can never separate them */}
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px' }}>
                  {BAR_HEIGHTS.map((height, index) => (
                    <Box
                      key={height}
                      component="span"
                      sx={{
                        width: '3px',
                        height: `${height}px`,
                        borderRadius: '1px',
                        backgroundColor: (theme) => {
                          if (difficultyLevel === undefined || index >= difficultyLevel) {
                            return theme.palette.mode === 'light' ? '#E4D3BC' : '#4A351F';
                          }
                          if (difficultyLevel === 1) return theme.palette.success.main;
                          return difficultyLevel === 2 ? theme.palette.warning.main : theme.palette.error.main;
                        },
                      }}
                    />
                  ))}
                </Box>
                <Box
                  component="span"
                  sx={{ color: (theme) => theme.palette.mode === 'light' ? 'primary.dark' : 'primary.main' }}
                >
                  {t(difficulty)}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <ChevronRightIcon
        sx={{
          flexShrink: 0,
          fontSize: 18,
          color: (theme) => theme.palette.mode === 'light' ? '#D3BFA4' : '#6B563C',
        }}
      />
    </Paper>
  );
};

/** The small dot that separates the parts of the metadata line. */
const Separator = () => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      width: '3px',
      height: '3px',
      borderRadius: '50%',
      backgroundColor: (theme) => theme.palette.mode === 'light' ? '#CBB89C' : '#5C452C',
    }}
  />
);

export default RecipeItem;
